import Dialog from '@mui/material/Dialog'
import Composer from 'components/Composer'
import { ComposerMediaContext } from 'components/ComposerMediaContext'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import {
  addComposerImages,
  addImagePosts,
  getLinkDetails,
  removeComposerImages,
  resetComposer,
  setComposerBulkUploadFileUrl,
  setComposerStatus
} from 'services/compose'
import { composerImagesSelector, composerIsEmptySelector } from 'services/compose/selectors'
import { COMPOSER_STATUS_KEY_GENERIC } from 'shared/types'
import StoreState, { StoreThunkDispatch } from 'store/state'
import styles from './Compose.pcss'

const SHARE_PARAMS = ['shareUrl', 'shareImageUrl', 'shareImageUrls']
const SHARE_IMAGE_URLS_PREFIX = 'shareImageUrls='

const decodeQueryValue = (value: string) => new URLSearchParams(`v=${value}`).get('v') ?? ''

// The extension encodes each image URL on its own and joins them with a literal '|' (',' in extension v1), so the list
// has to be split while still encoded - decoding first would also split a URL holding an encoded '|' or ',' of its own.
function parseShareImageUrls(search: string): string[] {
  const raw = search
    .replace(/^\?/, '')
    .split('&')
    .find(pair => pair.startsWith(SHARE_IMAGE_URLS_PREFIX))
    ?.slice(SHARE_IMAGE_URLS_PREFIX.length) ?? ''
  // NOTE: backward compatibility for PP extension v1 where separator was ','
  const separator = raw.includes('|') ? '|' : ','
  return raw.split(separator).map(decodeQueryValue).filter(Boolean)
}

export function Compose() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const store = useStore<StoreState>()
  const { dispatch: contextDispatch } = useContext(ComposerMediaContext)
  const navigate = useNavigate()
  const location = useLocation()
  const isEmpty = useSelector(composerIsEmptySelector)
  const prevLocationState = useRef<any>({})
  const handledSearch = useRef<string | undefined>(undefined)

  const [searchParams] = useSearchParams()
  const shareUrl = searchParams.get('shareUrl')
  const shareImageUrl = searchParams.get('shareImageUrl')
  const shareImageUrls = searchParams.get('shareImageUrls')
  const imageSource = searchParams.get('imageSource')
  const multi = searchParams.get('multi')
  const hasShareParams = SHARE_PARAMS.some(key => searchParams.has(key))

  useEffect(() => {
    // NOTE: every branch below clears `composer.images`, so reading them through `useSelector` and
    // listing them as a dependency re-ran this effect (and re-added the posts) on each dispatch.
    // `navigate` only drops the query params in a low priority transition, so it lost that race and
    // the import repeated until React bailed out. Import once per query instead, and read the
    // images at dispatch time.
    if (handledSearch.current === location.search) {
      return
    }
    handledSearch.current = location.search
    const images = composerImagesSelector(store.getState())

    // NOTE: values come out of `searchParams` already decoded - decoding them again throws on a literal `%`
    // and corrupts URLs that carry encoded characters of their own
    if (shareUrl) {
      dispatch(resetComposer())
      dispatch(getLinkDetails({ url: shareUrl }))
      dispatch(setComposerStatus({ text: shareUrl, html: shareUrl, network: COMPOSER_STATUS_KEY_GENERIC }))
    } else if (shareImageUrl) {
      dispatch(removeComposerImages({ ids: Object.keys(images), contextDispatch }))
      dispatch(addComposerImages({ items: [{ url: shareImageUrl }], contextDispatch }))
      if (imageSource) {
        const status = `Image source: ${imageSource}`
        dispatch(setComposerStatus({ text: status, html: status, network: COMPOSER_STATUS_KEY_GENERIC }))
      }
    } else if (shareImageUrls) {
      const urls = parseShareImageUrls(location.search)
      if (multi) {
        dispatch(addImagePosts(urls.map(url => ({ url }))))
      } else {
        dispatch(removeComposerImages({ ids: Object.keys(images), contextDispatch }))
        dispatch(addComposerImages({ items: urls.map(url => ({ url })), contextDispatch }))
      }
    }

    // Strip the share params even when they are all empty, so they don't stick around in the address bar and history
    if (hasShareParams) {
      navigate('/composer', { replace: true })
    }
  }, [
    contextDispatch, dispatch, hasShareParams, imageSource, location.search, multi, navigate, shareImageUrl, shareImageUrls, shareUrl, store
  ])

  useEffect(() => {
    if (location.state?.opener !== '/composer') {
      prevLocationState.current = location.state
    }
  }, [location.state])

  const onUnload = useCallback((event: Event) => {
    event.preventDefault()
    event.returnValue = false
    return ''
  }, [])

  useEffect(() => {
    if (__DEV__) {
      return
    }
    if (!isEmpty) {
      window.addEventListener('beforeunload', onUnload)
    }
    return () => {
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [isEmpty, onUnload])

  const close = (reset?: boolean, redirectUrl?: string) => {
    if (redirectUrl) {
      navigate(redirectUrl)
    } else if (prevLocationState.current) {
      const { opener, search, nextState } = prevLocationState.current
      navigate(opener || '/', { state: { search, ...nextState } })
    } else {
      navigate('/')
    }

    dispatch(setComposerBulkUploadFileUrl({ url: undefined }))
    if (reset) {
      dispatch(resetComposer())
    }
  }

  return (
    <Dialog
      open
      fullScreen
      disableEnforceFocus
      classes={{ root: styles.dialog, paper: styles.paper }}
      data-testid="composer-dialog"
    >
      <Composer onClose={close} />
    </Dialog>
  )
}

export default Compose
